import React, { useState, useEffect } from "react";
import Assistant from "../ai-tutor/Assistant/Assistant";
import { Passage, Question_Object } from "./comprehensionConstants";
import AudioPlayer from "../ai-tutor/AudioPlayer";
import styles from "./ComprehensionStyles.module.css";
import pkg from "@inrscr/coschool-ui-components";
import AssistantImg from "../../../assets/ai-tutor/assistant.svg";
import { QuestionsLayout } from "../questionLayouts/QuestionsLayout";
import { questionTypes } from "../../../utils/LearningPathUtils";
import { usePractice } from "../../../context/PracticeContext";
import { useTutor } from "../../../context/AiTutorContext";
import { generateNewChatId } from "../../../utils/TutorConstants";
import { matchFibAnswers } from "../../../utils/Helper";
import {
  getTimeDifference,
  isNotEmptyOrNull } from
"../../../utils/common-utils";

const ComprehensionLayout = (props) => {
  const { AssistantV2 } = pkg;
  const [openSubQuestionIndex, setOpenSubQuestionIndex] = useState(0);
  const [submittedAnswers, setSubmittedAnswers] = useState({});
  const [allQuestionsAnswered, setAllQuestionsAnswered] = useState(false);
  const [questionStartTimes, setQuestionStartTimes] = useState({});
  const [answerResponses, setAnswerResponses] = useState({});
  const [subQuestionChatIds, setSubQuestionChatIds] = useState({});
  const [displayQuestion, setDisplayQuestion] = useState(false);
  const {
    handleLongAnsStreamFlow,
    setIsDescAnsQues,
    handleComprehensionAnswerSubmit,
    setChatId,
    chatId
  } = usePractice();

  const {
    isComprehensionQuestion,
    setIsComprehensionQuestion,
    isComprehensionDescChat,
    setIsComprehensionDescChat
  } = useTutor();

  const descQuestionTypes = [
  questionTypes.short_answer,
  questionTypes.long_answer,
  questionTypes.very_short_answer];


  const getFilteredSubQuestions = () => {
    return (
      props?.question?.sub_question?.filter((subQues) =>
      [questionTypes?.scq, ...descQuestionTypes].includes(
        subQues?.question_type
      )
      ) || []);

  };

  const calculateAnswerResponse = async (
  subQues,
  answer,
  index,
  hashedIndexAnswer = null,
  selectedIndex = null) =>
  {
    const endTime = new Date();
    const startTime = questionStartTimes[index];
    const time_taken = await getTimeDifference(startTime, endTime);

    // Use hashedIndexAnswer if available (for SCQ)
    const answerArr = hashedIndexAnswer ? [hashedIndexAnswer] : [answer];
    const useHashed = Boolean(hashedIndexAnswer);

    const response = matchFibAnswers(
      isNotEmptyOrNull(subQues?.answers_index_hashed_keys) ?
      subQues?.answers_index_hashed_keys :
      isNotEmptyOrNull(subQues?.answers_key) ?
      subQues?.answers_key :
      subQues?.answers_layout,
      answerArr,
      time_taken,
      isNotEmptyOrNull(subQues?.answers_key),
      subQues?.options_layout,
      useHashed
    );

    return {
      response,
      time_taken,
      hashedIndexAnswer,
      selectedIndex
    };
  };

  useEffect(() => {
    const filteredSubQuestions = getFilteredSubQuestions();
    const initialTimes = {};
    filteredSubQuestions.forEach((_, index) => {
      initialTimes[index] = new Date();
    });
    setQuestionStartTimes(initialTimes);
  }, [props.question]);

  const toggleQuestion = (index) => {
    if (openSubQuestionIndex === index) {
      if (!submittedAnswers[index]) {
        props.onAnswerChanged("", props.question, true, false);
      }
      setOpenSubQuestionIndex(null);
      setIsDescAnsQues(false);
    } else {
      const filteredSubQuestions = getFilteredSubQuestions();
      const subQuestion = filteredSubQuestions[index];
      const isDescQues = descQuestionTypes.includes(subQuestion?.question_type);
      setIsDescAnsQues(isDescQues && !submittedAnswers[index]);
      setOpenSubQuestionIndex(index);
      setQuestionStartTimes((prev) => ({
        ...prev,
        [index]: new Date()
      }));
    }
  };

  const handleNextQuestion = () => {
    const filteredSubQuestions = getFilteredSubQuestions();
    const nextIndex = openSubQuestionIndex + 1;
    if (nextIndex < filteredSubQuestions.length) {
      const nextSubQuestion = filteredSubQuestions[nextIndex];
      const isNextDescQues = descQuestionTypes.includes(
        nextSubQuestion?.question_type
      );
      setIsDescAnsQues(isNextDescQues);
      setOpenSubQuestionIndex(nextIndex);
      setQuestionStartTimes((prev) => ({
        ...prev,
        [nextIndex]: new Date()
      }));
    }
  };

  const handleAnswerSubmit = async (
  index,
  answer,
  hashedIndexAnswer = null,
  selectedIndex = null) =>
  {
    const filteredSubQuestions = getFilteredSubQuestions();
    const currentSubQuestion = filteredSubQuestions[index];
    const isDescQues = descQuestionTypes.includes(
      currentSubQuestion?.question_type
    );

    if (isDescQues) {
      const newChatId = generateNewChatId();
      setChatId(newChatId);
      setSubQuestionChatIds((prev) => ({
        ...prev,
        [index]: newChatId
      }));
      handleLongAnsStreamFlow(answer, [], index);
    }

    const { response } = await calculateAnswerResponse(
      currentSubQuestion,
      answer,
      index,
      hashedIndexAnswer,
      selectedIndex
    );

    setAnswerResponses((prev) => ({
      ...prev,
      [index]: response
    }));

    setSubmittedAnswers((prev) => ({
      ...prev,
      [index]: hashedIndexAnswer ? hashedIndexAnswer : answer
    }));

    // Call onAnswerChanged with the same signature as normal flow
    props.onAnswerChanged(
      answer,
      currentSubQuestion,
      hashedIndexAnswer,
      selectedIndex,
      props?.isComprehension,
      false // allSubQuestionsAnswered is handled in the effect below
    );
  };

  useEffect(() => {
    setIsComprehensionQuestion(true);
    const filteredSubQuestions = getFilteredSubQuestions();
    const allAnswered = filteredSubQuestions?.every(
      (_, index) => submittedAnswers[index] !== undefined
    );
    setAllQuestionsAnswered(allAnswered);

    if (allAnswered) {
      const answersArray = filteredSubQuestions.map(async (subQues, index) => {
        // For SCQ, submittedAnswers[index] is the hashed answer
        const hashedIndexAnswer = isNotEmptyOrNull(
          subQues?.answers_index_hashed_keys
        ) ?
        submittedAnswers[index] :
        null;
        const answer = hashedIndexAnswer ? null : submittedAnswers[index];
        const { response, time_taken } = await calculateAnswerResponse(
          subQues,
          answer,
          index,
          hashedIndexAnswer
        );

        const isDescQues = descQuestionTypes.includes(subQues?.question_type);

        return {
          question_id: subQues?.version_uuid,
          instance_id: subQues?.version_uuid,
          user_answer: JSON.stringify(response),
          result: response.overAllResult,
          time_taken: time_taken,
          chat_id: isDescQues ? subQuestionChatIds[index] : null
        };
      });

      // Handle the array of promises
      Promise.all(answersArray).then((completedAnswers) => {
        handleComprehensionAnswerSubmit(completedAnswers);
        props.onAnswerChanged(completedAnswers, props.question, true, true);
      });
    }
  }, [submittedAnswers, props.question]);

  // Add useEffect to handle descriptive answer submission
  useEffect(() => {
    if (isNotEmptyOrNull(isComprehensionDescChat)) {
      handleAnswerSubmit(openSubQuestionIndex, isComprehensionDescChat);
      setIsComprehensionDescChat("");
      setIsDescAnsQues(false);
    }
  }, [isComprehensionDescChat]);

  return (
    <div className={styles?.mainContainer}>
      <AssistantV2
        loaderText="Take a deep breath! Brace up for comprehension question. Read the passage and answer the questions"
        finalText="Once you select your answer and hit Next, you won't be able to change it - so take a moment to be sure before locking it in!"
        hideVin={false}
        assistantImg={AssistantImg}
        onTypingComplete={() => {
          setDisplayQuestion(true);
        }} />

      {displayQuestion &&
      <>
          <div className={styles.passageContainer}>
            <Assistant
            text={props?.question?.question_layout}
            hideVin={true}
            isCopy={false}
            isQuestion={false}
            isComprehension={true}
            audioPlayer={
            window?.ENV?.ENABLE_VOICE === "true" ?
            () => <AudioPlayer /> :
            undefined
            } />

          </div>

          <div className={styles.subQuestionContainer}>
            {getFilteredSubQuestions().map((subQues, index) =>
          <QuestionsLayout
            key={index}
            questionIndex={index}
            questionObject={subQues}
            userAnswer={
            // For SCQ, pass the hashed answer; for desc, pass the answer text
            isNotEmptyOrNull(subQues?.answers_index_hashed_keys) ?
            submittedAnswers[index] :
            answerResponses[index]
            }
            onAnswerChanged={(
            answer,
            questionObj,
            hashedIndexAnswer,
            selectedIndex,
            isComprehension) =>

            handleAnswerSubmit(
              index,
              answer,
              hashedIndexAnswer,
              selectedIndex
            )
            }
            isDontAllow={props?.isDontAllow}
            allowToCopy={props?.allowToCopy}
            isFactualBasket={props?.isFactualBasket}
            taskType={props?.taskType}
            isAnswerSubmitted={submittedAnswers[index] !== undefined}
            displayedQues={props?.displayedQues}
            isComprehension={props?.isComprehension}
            isOpen={openSubQuestionIndex === index}
            toggleOpen={() => toggleQuestion(index)}
            onAnswerSubmit={(answer) => handleAnswerSubmit(index, answer)}
            isLastQuestion={index === getFilteredSubQuestions().length - 1}
            allQuestionsAnswered={allQuestionsAnswered}
            onNextQuestion={handleNextQuestion} />

          )}
          </div>
        </>
      }
    </div>);

};

export default ComprehensionLayout;